import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, ZONA_HORARIA, centavos, fechaEnNicaragua, formatMoney, hoy, sumarDias, type Centavos, type Role } from '@abastece/shared';
import { fiadoDeVenta, type Devolucion, type SesionCaja, type Venta } from '@abastece/core';
import { db, type LocalEvent } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { busquedaDeControl, type DiasControl, type Motivo, type Registro, type RegistroConteo } from '../lib/control.js';
import { tieneDevoluciones } from '../lib/historial.js';
import { useMonedaDeLosLibros } from '../lib/libros.js';
import { datosNegocio } from '../lib/recibo.js';
import { usePuede } from '../lib/sesion.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaRol } from '../i18n/dominio.js';
import { CorregirConteo } from '../ui/CorregirConteo.js';
import { ExplicarFaltante } from '../ui/Correcciones.js';
import { enDolares } from '../ui/CuentasDeCaja.js';
import { DetalleEvento } from '../ui/DetalleEvento.js';
import { FilaMoneda } from '../ui/ElegirMoneda.js';
import { Ico, type NombreIcono, type Tinte } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { CifrasDePersona, ElegirDias, useControl } from './Control.js';
import { DetalleVenta } from './Ventas.js';

/** How many records show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 100;

type Ver = 'todo' | 'caja' | 'ventas';
const VER: { clave: Ver; nombre: Clave }[] = [
  { clave: 'todo', nombre: 'controlPersona.ver.todo' },
  { clave: 'caja', nombre: 'controlPersona.ver.caja' },
  { clave: 'ventas', nombre: 'controlPersona.ver.ventas' },
];

const MOTIVO: Record<Motivo, Clave> = {
  lista: 'control.motivo.lista',
  rapidas: 'control.motivo.rapidas',
  margen: 'control.motivo.margen',
  fiado: 'control.motivo.fiado',
  noEsDueno: 'control.motivo.noEsDueno',
  otroNombre: 'control.motivo.otroNombre',
};

/** What a row needs that its record does not carry, read for the rows on the screen. */
interface Datos {
  ventas: ReadonlyMap<string, Venta>;
  devoluciones: ReadonlyMap<string, Devolucion>;
  sesiones: ReadonlyMap<string, SesionCaja>;
  eventos: ReadonlyMap<string, LocalEvent>;
  clientes: ReadonlyMap<string, string>;
}

const porId = <T extends { id: string }>(filas: (T | undefined)[]) =>
  new Map(filas.filter((f): f is T => f !== undefined).map((f) => [f.id, f]));

/** A sheet over the page: a sale, a count's record, a count corrected, a shortage explained. */
type Hoja =
  | { tipo: 'venta'; ventaId: string }
  | { tipo: 'registro'; eventoId: string }
  | { tipo: 'corregir'; sesionId: string; cual: 'apertura' | 'corte' }
  | { tipo: 'explicar'; eventoId: string; nombre: string };

/**
 * Lo que hizo una persona: what their card on «Control» adds up, a record at a time.
 *
 * The owner (2026-10-23): «in the control page you should be able to click the name of the user and
 * see a list of their till openings/closings and sales that you can also click to see the details.
 * The owner should be able to make corrections from this list as well». Over the days the card was
 * read over, newest first, day by day: each opening of a till, each handover and corte of a drawer
 * they counted or had, each time the owner received what their till handed over, and each sale,
 * void and return, read by the rules the card is (`registros` in `lib/control.ts`), so a sale rung
 * on a device somebody else was using is not theirs here either.
 *
 * A sale, or a void or a return of one, opens the sale's sheet as «Historial de ventas» does: its
 * receipt again, a return, a void. A count opens its record as «Bitácora» does. And the owner
 * corrects from the list itself: «Corregir la apertura» and «Corregir el corte» under each, as under
 * each shift of «Turnos anteriores», and «Explicar» under a shortage still unexplained, as in
 * «Faltantes de caja».
 */
export function ControlPersona() {
  const t = useT();
  const navigate = useNavigate();
  const { usuarioId } = useParams({ from: '/control/$usuarioId' });
  const busqueda = busquedaDeControl(useSearch({ from: '/control/$usuarioId' }));
  const dias: DiasControl = busqueda.dias ?? 7;
  const [ver, setVer] = useState<Ver>('todo');
  const [cuantos, setCuantos] = useState(LOTE);
  const [hoja, setHoja] = useState<Hoja | null>(null);
  const elegirDias = (d: DiasControl) => {
    setCuantos(LOTE);
    void navigate({
      to: '/control/$usuarioId',
      params: { usuarioId },
      search: busquedaDeControl({ dias: d }),
      replace: true,
      viewTransition: false,
    });
  };
  const control = useControl(dias);
  // null: nobody by that id on this phone; undefined: not read yet.
  const persona = useLiveQuery(async () => (await db.usuarios.get(usuarioId)) ?? null, [usuarioId], undefined);
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const dispositivos = useLiveQuery(() => db.dispositivos.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  // A count put right, and a shortage explained: the owner's, as on «Turnos anteriores» and «Control».
  const puedeCorregir = usePuede('configurar');
  const libros = useMonedaDeLosLibros();
  const dinero = (c: number) => libros.dinero(centavos(c));

  const fila = control?.personas.find((p) => p.usuarioId === usuarioId);
  const todos = control?.registros.get(usuarioId);
  const lista = useMemo(
    () =>
      [...(todos ?? [])]
        .reverse()
        .filter((r) => ver === 'todo' || (ver === 'caja') === (r.tipo === 'conteo')),
    [todos, ver],
  );
  const visibles = useMemo(() => lista.slice(0, cuantos), [lista, cuantos]);
  const datos = useLiveQuery(async (): Promise<Datos> => {
    const ventaIds = [...new Set(visibles.flatMap((r) => (r.tipo === 'conteo' ? [] : [r.ventaId])))];
    const sesionIds = [...new Set(visibles.flatMap((r) => (r.tipo === 'conteo' ? [r.sesionId] : [])))];
    const [ventas, devoluciones, sesiones, eventos] = await Promise.all([
      db.ventas.bulkGet(ventaIds),
      db.devoluciones.bulkGet(visibles.flatMap((r) => (r.tipo === 'devolucion' ? [r.devolucionId] : []))),
      db.sesiones.bulkGet(sesionIds),
      db.eventos.bulkGet(visibles.flatMap((r) => (r.tipo === 'conteo' ? [r.eventoId] : []))),
    ]);
    const clientes = await db.clientes.bulkGet([...new Set(ventas.flatMap((v) => (v?.clienteId ? [v.clienteId] : [])))]);
    return {
      ventas: porId(ventas),
      devoluciones: porId(devoluciones),
      sesiones: porId(sesiones),
      eventos: new Map(eventos.flatMap((e) => (e ? [[e.id, e] as const] : []))),
      clientes: new Map(clientes.flatMap((c) => (c ? [[c.id, c.nombre] as const] : []))),
    };
  }, [visibles]);

  const nombre = (id: string) => usuarios.find((u) => u.id === id)?.nombre ?? t('ventas.alguien');
  const equipo = (id: string | undefined) => {
    const d = dispositivos.find((x) => x.id === id);
    return d ? `${d.nombre} (${d.codigo})` : undefined;
  };

  if (persona === undefined) return null;
  if (persona === null) {
    return (
      <Pagina titulo={t('control.titulo')} volverA="/control" volverBusqueda={busqueda}>
        <Vacio mensaje={t('controlPersona.noExiste')} icono="personal" />
      </Pagina>
    );
  }

  const hoyEs = hoy();
  const ayer = sumarDias(hoyEs, -1);
  const conDia = new Intl.DateTimeFormat(localeActual(), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  const dia = (fecha: string) =>
    fecha === hoyEs ? t('periodo.hoy') : fecha === ayer ? t('periodo.ayer') : conDia.format(new Date(`${fecha}T00:00:00Z`));
  // Day by day, in the shop's time, as the rows come: newest first.
  const porDia: { fecha: string; registros: Registro[] }[] = [];
  for (const r of visibles) {
    const fecha = fechaEnNicaragua(r.cuando);
    const ultimo = porDia[porDia.length - 1];
    if (ultimo?.fecha === fecha) ultimo.registros.push(r);
    else porDia.push({ fecha, registros: [r] });
  }
  const nada = ver === 'caja' ? 'controlPersona.nadaCaja' : ver === 'ventas' ? 'controlPersona.nadaVentas' : 'controlPersona.nada';

  return (
    <Pagina titulo={persona.nombre} subtitulo={etiquetaRol(persona.rol as Role)} volverA="/control" volverBusqueda={busqueda}>
      <ElegirDias dias={dias} onElegir={elegirDias} />
      <FilaMoneda moneda={libros.moneda} onElegir={libros.elegir} tasa={libros.tasa} className="-mt-1 mb-4" />

      {!control ? null : (
        <>
          {fila && (
            <section className="tarjeta mb-5">
              <CifrasDePersona fila={fila} rol={persona.rol} dinero={dinero} />
            </section>
          )}

          {/* Nothing to narrow down over a person with nothing in these days: only that it is so. */}
          {(todos?.length ?? 0) > 0 && (
            <>
              <div role="radiogroup" aria-label={t('controlPersona.ver')} className="mb-2 flex gap-1 rounded-xl bg-slate-800 p-1">
                {VER.map((v) => (
                  <button
                    key={v.clave}
                    type="button"
                    role="radio"
                    aria-checked={ver === v.clave}
                    onClick={() => {
                      setVer(v.clave);
                      setCuantos(LOTE);
                    }}
                    data-sin-icono="pestana"
                    data-control-ver={v.clave}
                    className={`min-h-10 flex-1 rounded-lg text-sm font-medium transition ${
                      ver === v.clave ? 'bg-slate-600 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }`}
                  >
                    {t(v.nombre)}
                  </button>
                ))}
              </div>
              <p className="mb-4 text-xs text-slate-500">{t('controlPersona.ayuda')}</p>
            </>
          )}

          {lista.length === 0 ? (
            <Vacio mensaje={t(nada)} icono={ver === 'ventas' ? 'ventas' : 'caja'} />
          ) : (
            <div className="flex flex-col gap-4" data-control-registros>
              {porDia.map((d) => (
                <section key={d.fecha} data-control-dia={d.fecha}>
                  <h2 className="rotulo">{dia(d.fecha)}</h2>
                  <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1">
                    {d.registros.map((r) => (
                      <FilaRegistro
                        key={`${r.eventoId}:${r.tipo}`}
                        r={r}
                        de={usuarioId}
                        datos={datos}
                        nombre={nombre}
                        equipo={equipo}
                        dinero={dinero}
                        puedeCorregir={puedeCorregir}
                        onHoja={setHoja}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
          {lista.length > cuantos && (
            <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
              <Ico icono="desplegar" />
              {t('comun.verMas')}
            </button>
          )}
        </>
      )}

      {hoja?.tipo === 'venta' && <DetalleVenta ventaId={hoja.ventaId} negocio={datosNegocio(config)} onCerrar={() => setHoja(null)} />}
      {hoja?.tipo === 'registro' && <DetalleEvento eventoId={hoja.eventoId} onCerrar={() => setHoja(null)} />}
      {hoja?.tipo === 'corregir' && <CorregirConteo sesionId={hoja.sesionId} cual={hoja.cual} onCerrar={() => setHoja(null)} />}
      {hoja?.tipo === 'explicar' && <ExplicarFaltante eventoId={hoja.eventoId} nombre={hoja.nombre} onCerrar={() => setHoja(null)} />}
    </Pagina>
  );
}

/**
 * Words that stay together on their line: «8:24 a. m.», «C$ 2,632.00», «3 línea(s)». At 320 px a
 * row's words have a third of the screen, and the time and the amounts broke across two lines.
 */
const junto = (texto: string) => texto.replace(/ /g, '\u00a0');
/** An amount whole on its line, «C$» never apart from its figure, and the words around it free. */
const entero = (texto: string) => texto.replace(/(C\$|US\$) /g, '$1\u00a0');

/** The time of day a record was made, in the shop's time. */
const hora = (iso: string) =>
  junto(new Date(iso).toLocaleTimeString(localeActual(), { hour: 'numeric', minute: '2-digit', timeZone: ZONA_HORARIA }));

/** Córdobas, and dollars beside them when there are any: a drawer counted. */
const contado = (cordobas: number, dolares: number) =>
  dolares > 0 ? `${formatMoney(cordobas as Centavos)} + ${enDolares(dolares as Centavos)}` : formatMoney(cordobas as Centavos);

/** What a count counted: an opening and a corte as the owner corrected them, the others as typed. */
function loContado(r: RegistroConteo, datos: Datos | undefined): string | undefined {
  const s = datos?.sesiones.get(r.sesionId);
  const p = (datos?.eventos.get(r.eventoId)?.payload ?? {}) as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === 'number' ? v : 0);
  if (r.conteo === 'apertura') return s ? contado(s.fondoInicial, s.fondoDolares ?? 0) : undefined;
  if (r.conteo === 'corte') return s?.conteoCordobas != null ? contado(s.conteoCordobas, s.conteoDolares ?? 0) : undefined;
  if (r.conteo === 'entrega') return typeof p.conteoCordobas === 'number' ? contado(p.conteoCordobas, n(p.conteoDolares)) : undefined;
  return typeof p.contadoCordobas === 'number' ? contado(p.contadoCordobas, n(p.contadoDolares)) : undefined;
}

/** A count's name on the page of `de`: whoever counted it, or whoever had the drawer. */
function tituloConteo(t: ReturnType<typeof useT>, r: RegistroConteo, de: string, nombre: (id: string) => string): string {
  const propio = r.hizo === r.deQuien;
  switch (r.conteo) {
    case 'apertura':
      return t('controlPersona.abrio');
    case 'entrega':
      return r.hizo === de ? t('controlPersona.recibioDe', { nombre: nombre(r.deQuien) }) : t('controlPersona.entregoA', { nombre: nombre(r.hizo) });
    case 'corte':
      return propio
        ? t('controlPersona.corte')
        : r.hizo === de
          ? t('controlPersona.corteDe', { nombre: nombre(r.deQuien) })
          : t('controlPersona.cortePor', { nombre: nombre(r.hizo) });
    case 'recibido':
      return r.hizo === de
        ? t('controlPersona.recibioLoDe', { nombre: nombre(r.deQuien) })
        : t('controlPersona.recibidoPor', { nombre: nombre(r.hizo) });
  }
}

const ICONO: Record<Registro['tipo'], { icono: NombreIcono; tinte: Tinte }> = {
  conteo: { icono: 'caja', tinte: 'marca' },
  venta: { icono: 'ventas', tinte: 'neutro' },
  anulacion: { icono: 'anular', tinte: 'rosa' },
  devolucion: { icono: 'devolver', tinte: 'cielo' },
};

function FilaRegistro({
  r,
  de,
  datos,
  nombre,
  equipo,
  dinero,
  puedeCorregir,
  onHoja,
}: {
  r: Registro;
  /** Whose page it is. */
  de: string;
  datos: Datos | undefined;
  nombre: (id: string) => string;
  equipo: (id: string | undefined) => string | undefined;
  dinero: (c: number) => string;
  puedeCorregir: boolean;
  onHoja: (h: Hoja) => void;
}) {
  const t = useT();
  const { icono, tinte } = ICONO[r.tipo];
  let titulo: string;
  let detalle: (string | undefined)[];
  const marcas: { texto: string; clase: string }[] = [];
  let cifra: { texto: string; clase: string } | undefined;
  let apagada = false;
  const acciones: { clave: string; texto: string; icono: NombreIcono; hoja: Hoja }[] = [];

  if (r.tipo === 'conteo') {
    const s = datos?.sesiones.get(r.sesionId);
    titulo = tituloConteo(t, r, de, nombre);
    const cuanto = loContado(r, datos);
    detalle = [hora(r.cuando), equipo(s?.dispositivoId), cuanto && entero(t('controlPersona.conto', { monto: cuanto }))];
    cifra =
      r.diferencia === 0
        ? { texto: t('caja.cuadro'), clase: 'text-emerald-400' }
        : { texto: `${r.diferencia < 0 ? '−' : '+'}${dinero(Math.abs(r.diferencia))}`, clase: r.diferencia < 0 ? 'text-rose-300' : 'text-amber-300' };
    // Under the count, not beside its figure: beside it, «C$ 20.00 ya explicado» left the row's
    // name a word a line at 320 px.
    if (r.corregido) marcas.push({ texto: t('controlPersona.corregido'), clase: 'text-slate-400' });
    if (r.explicado > 0) marcas.push({ texto: entero(t('explicar.explicado', { monto: dinero(r.explicado) })), clase: 'text-slate-400' });
    if (puedeCorregir && (r.conteo === 'apertura' || r.conteo === 'corte')) {
      const cual = r.conteo;
      acciones.push({
        clave: cual,
        texto: t(cual === 'apertura' ? 'conteoCaja.tituloApertura' : 'conteoCaja.tituloCorte'),
        icono: 'editar',
        hoja: { tipo: 'corregir', sesionId: r.sesionId, cual },
      });
    }
    if (puedeCorregir && r.porExplicar > 0) {
      acciones.push({ clave: 'explicar', texto: t('explicar.boton'), icono: 'gastos', hoja: { tipo: 'explicar', eventoId: r.eventoId, nombre: nombre(r.deQuien) } });
    }
  } else {
    const v = datos?.ventas.get(r.ventaId);
    const ticket = v?.ticket ?? '';
    if (r.tipo === 'venta') {
      titulo = ticket;
      detalle = [hora(r.cuando), v?.clienteId ? datos?.clientes.get(v.clienteId) : undefined, v && junto(t('controlPersona.lineas', { n: v.lineas.length }))];
      cifra = v && { texto: dinero(v.total), clase: 'font-medium' };
      apagada = v?.anulada ?? false;
      if (v) {
        if (v.pagos.some((p) => p.estado === 'pendiente') && !v.anulada) marcas.push({ texto: t('ventas.transferenciaSinConfirmar'), clase: 'text-amber-400' });
        if (fiadoDeVenta(v) > 0 && !v.anulada) marcas.push({ texto: t('ventas.fiado'), clase: 'text-amber-300' });
        if (tieneDevoluciones(v)) marcas.push({ texto: t('ventas.conDevolucion'), clase: 'text-sky-300' });
        if (v.anulada) marcas.push({ texto: t('ventas.anulada'), clase: 'text-rose-400' });
      }
      // What «Sin la autorización del dueño» lists it for, in its words.
      if (r.motivos.length > 0) marcas.push({ texto: r.motivos.map((m) => t(MOTIVO[m])).join(' · '), clase: 'text-rose-300' });
    } else if (r.tipo === 'anulacion') {
      titulo = t('controlPersona.anulo', { ticket });
      detalle = [hora(r.cuando), v?.motivoAnulacion ?? undefined];
      cifra = v && { texto: dinero(v.total), clase: 'text-slate-300' };
    } else {
      const dev = datos?.devoluciones.get(r.devolucionId);
      titulo = t('controlPersona.devolucion', { ticket: dev?.ticket ?? ticket });
      detalle = [hora(r.cuando), dev?.motivo];
      cifra = dev && { texto: dinero(dev.total), clase: 'text-slate-300' };
    }
  }

  const abrir: Hoja = r.tipo === 'conteo' ? { tipo: 'registro', eventoId: r.eventoId } : { tipo: 'venta', ventaId: r.ventaId };
  return (
    <li className="px-1" data-control-registro={r.tipo} data-control-evento={r.eventoId}>
      {/* Lighting under the mouse a little wider than its words, as the rows of «Efectivo» do. */}
      <button
        type="button"
        onClick={() => onHoja(abrir)}
        data-sin-icono="fila"
        className={`-mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-white/5 ${apagada ? 'opacity-60' : ''}`}
      >
        <span className={`chip chip-${tinte} mt-0.5 size-8 rounded-lg`}>
          <Ico icono={icono} className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="tabular block text-sm font-medium wrap-break-word">{titulo}</span>
          {/* What it is said to be, «fiado», «corregido», after it, and flush left on a line of its own when it wraps. */}
          <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs">
            <span className="tabular min-w-0 wrap-break-word text-slate-500">{detalle.filter(Boolean).join(' · ')}</span>
            {marcas.map((m) => (
              <span key={m.texto} className={`min-w-0 wrap-break-word ${m.clase}`}>
                {m.texto}
              </span>
            ))}
          </span>
        </span>
        {cifra && <span className={`tabular shrink-0 text-right text-sm whitespace-nowrap ${cifra.clase}`}>{cifra.texto}</span>}
      </button>
      {acciones.length > 0 && (
        <div className="-mt-1 flex flex-wrap gap-x-4 gap-y-1 pb-2.5 pl-11">
          {acciones.map((a) => (
            <button
              key={a.clave}
              type="button"
              className="btn-texto gap-1 text-xs text-slate-300"
              onClick={() => onHoja(a.hoja)}
              data-control-accion={a.clave}
            >
              <Ico icono={a.icono} className="size-3.5" />
              {a.texto}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
